/**
 * Balance tables hand-ported from bot functions whose modules cannot be
 * imported as data (they pull Discord/Firebase). Each table names its bot
 * source. Changing a value here is a balance change: record it in
 * docs/10-parity-notes.md (ADR-010).
 */

/** Adventures energy regeneration (`rpg/adventures/energy.ts#ENERGY_REGEN_MS`). */
export const ENERGY_REGEN_MS = 10 * 60 * 1000;

/** Duplicate cards needed per item level, by tier (`data/items.ts#itemNextLevel`). */
export const UPGRADE_DUPLICATES: number[][] = [
  [10, 50, 100, 150, 200, 250, 300, 350, 400, 500, 600, 700, 800, 900, 1000],
  [10, 40, 80, 120, 160, 200, 240, 280, 320, 360, 400, 500, 600, 700, 800],
  [10, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 400, 500, 600, 700],
  [10, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200, 300, 400, 500, 600],
  [2, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 25, 30, 35, 40],
  [2, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
  [2, 1, 1, 1, 1, 2, 2, 3, 3, 3, 4, 4, 4, 4, 5],
];

const P1 = [10000, 50000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 500000, 600000, 700000, 800000, 900000, 1000000];
/** Fakecoin price per item level, by tier (`data/items.ts#itemNextLevel`). */
export const UPGRADE_PRICES: number[][] = [
  P1,
  P1,
  P1,
  P1,
  [100000, 200000, 300000, 400000, 500000, 600000, 700000, 800000, 900000, 1000000, 1100000, 1200000, 1300000, 1400000, 1500000],
  [1000000, 2000000, 3000000, 4000000, 5000000, 6000000, 7000000, 8000000, 9000000, 10000000, 11000000, 12000000, 13000000, 14000000, 15000000],
  [10000000, 11000000, 12000000, 13000000, 14000000, 15000000, 16000000, 17000000, 18000000, 19000000, 20000000, 21000000, 22000000, 23000000, 24000000],
];

/** Mission durations (`rpg/idle.ts#timer`): hours and lootcoin roll ratio. */
export const MISSION_TIMERS: { time: number; ratio: number }[] = [
  { time: 0.25, ratio: 20 },
  { time: 0.5, ratio: 16 },
  { time: 2, ratio: 8 },
  { time: 6, ratio: 5 },
  { time: 12, ratio: 3 },
  { time: 24, ratio: 2 },
];

/** Mission reward tiers (`rpg/idle.ts#expeRewards`) — only the last entry is read by the bot. */
export const MISSION_REWARD_LAST = { money: 150, lootbox: 25, shopcoin: 0 } as const;

/** 15-day login calendar (`rpg/daily.ts#rewards`). */
export const DAILY_REWARDS: { money: number; lootbox: number; shopcoin: number }[] = [
  { money: 100, lootbox: 0, shopcoin: 0 },
  { money: 150, lootbox: 0, shopcoin: 0 },
  { money: 200, lootbox: 0, shopcoin: 0 },
  { money: 250, lootbox: 0, shopcoin: 0 },
  { money: 300, lootbox: 0, shopcoin: 0 },
  { money: 350, lootbox: 0, shopcoin: 0 },
  { money: 400, lootbox: 1, shopcoin: 0 },
  { money: 450, lootbox: 0, shopcoin: 0 },
  { money: 500, lootbox: 0, shopcoin: 0 },
  { money: 550, lootbox: 0, shopcoin: 0 },
  { money: 600, lootbox: 0, shopcoin: 0 },
  { money: 650, lootbox: 0, shopcoin: 0 },
  { money: 700, lootbox: 0, shopcoin: 0 },
  { money: 750, lootbox: 2, shopcoin: 0 },
  { money: 1000, lootbox: 3, shopcoin: 100 },
];

/** Summon amount presets (`rpg/summon.ts#getSummonRows`) plus 1 for the web. */
export const SUMMON_PRESETS = [1, 10, 100, 500, 1000] as const;

/** World boss (`rpg/boss/{state,spawn,rewards}.ts`). */
export const BOSS = {
  /** `computeBossHp`: floor(hpBase · e^((level + 1) / hpScale)). */
  hpBase: 25000,
  hpScale: 75,
  /** Spawn interval in seconds: 3–6 h by day, 6–8 h when spawning between 0:00 and 8:00 (`startEvent`). */
  day: { min: 10800, max: 21600 },
  night: { min: 21600, max: 28800 },
  nightUntilHour: 8,
  /** `distributeBossRewards`: base money = floor(moneyPerLevel · boss level) · (1 + (player level − 100) / 200). */
  moneyPerLevel: 30,
  /** One lootcoin when getRandomInt(0, lootRate) = 0; else shopcoins (1–14) when getRandomInt(0, shopRate) = 0. */
  lootRate: 20,
  shopRate: 166,
  shop: { min: 1, max: 15 },
  /** Legendary coin instead of the loot roll when level % every = 0 and level > after. */
  legendary: { every: 100, after: 250 },
} as const;

/** Battlepass (`rpg/battlepass/{data,rewards}.ts`): `getQuantity` multipliers (premium scales the level term). */
export const BATTLEPASS = {
  /** XP cost of every tier past the reward list (`getCost`). */
  beyondListCost: 1000,
  base: { one: 490, more: 900 },
  fakecoin: 25,
  lootcoin: 0.0375,
  levelDivisor: 25,
  shopcoin: 150,
} as const;

/**
 * Chests (`rpg/chest.ts#chestMultipliers` + `chestListData`), hand-ported:
 * importing rpg/chest.ts would initialize Firebase. Rewards are
 * `calculateChestRewards`: floor(2500 · fakecoin · (1 + level/25) · v),
 * floor(2.5 · lootcoin · (1 + level/25) · v) with v ∈ [0.8, 1.2), and
 * getRandomInt(1, nuggets + 1) nuggets.
 */
export const CHESTS: Record<string, { fakecoin: number; lootcoin: number; nuggets: number; emoji: string }> = {
  wood: { fakecoin: 1, lootcoin: 1, nuggets: 0, emoji: '<:WoodenChest:1072919112021458994>' },
  silver: { fakecoin: 2, lootcoin: 2, nuggets: 0, emoji: '<:SilverChest:1072923520960573460>' },
  gold: { fakecoin: 4, lootcoin: 4, nuggets: 1, emoji: '<:GoldenChest:1072919389298503680>' },
  enchanted: { fakecoin: 6, lootcoin: 6, nuggets: 2, emoji: '<:EnchantedChest:1072919397271875614>' },
  dofus: { fakecoin: 5, lootcoin: 5, nuggets: 3, emoji: '' },
  mythic: { fakecoin: 8, lootcoin: 8, nuggets: 5, emoji: '<:MythicChest:1072920968961462384>' },
  shushu: { fakecoin: 10, lootcoin: 10, nuggets: 5, emoji: '<:ShushuCodex:1073636282674196543>' },
};

/** Exchanges (`rpg/play/button-routes.ts#exchange`): 1000 nuggets → mythic chest, 6 codex pages → Shushu chest. */
export const EXCHANGES = {
  nuggets: { cost: 1000, chest: 'mythic' },
  pages: { cost: 6, chest: 'shushu' },
} as const;

/** Dofus roulette (`rpg/attributes/{math,roulette}.ts`): `1 + premium.roulette + vote.toutou` rolls per 2-hour window. */
export const DOFUS_ROLL = {
  maxRolls: 1,
  /** `getRollMultiplier`: 7–10 on the window's first roll, 1–3 after. */
  first: { min: 7, max: 11 },
  next: { min: 1, max: 4 },
  /** Ébène only on a first roll: getRandomInt(0, 166) = 0. */
  sixRate: 166,
  /** `TIER_THRESHOLDS`: tier hit when getRandomInt(0, n) = 0. */
  thresholds: { two: 4, three: 12, four: 36, five: 72 },
  /** Fakecoins per Dofus won, by tier; Ébène pays a flat 10 000. */
  money: { one: 50, two: 150, three: 300, four: 500, five: 750, six: 10000 },
  /** `MAX_UPGRADE_DOFUS`: attribute levels bought by one "max" upgrade. */
  maxUpgrade: 2000,
  /** Orbs spent by an attribute reset (`fillAttributeResetConfirm`). */
  resetCost: 1,
} as const;

/** Arena (`rpg/pvp/{state,rewards,combat,season}.ts`). */
export const ARENA = {
  /** `ATTACK_LIMIT`: counted attacks per day; a target only once a day. */
  attackLimit: 3,
  /** Daily reset hour of `resetAttack` and of the season lock (local). */
  resetHour: 7,
  /** Fights stop after this many strikes (`performAttack` limit). */
  maxTurns: 50,
  /** League index of the "legend" rank (honor is a flat 100 between legends). */
  legendRank: 5,
  legendHonor: 100,
} as const;

/**
 * Minigames played with the bot's rules: `shop/bet.ts`,
 * `dollarp/notif.ts#generateCote`, `minigames/spot.ts`. Chafer and larva
 * races wait for a lobby (P6).
 */
export const MINIGAMES = {
  bet: {
    minBid: 5,
    /** Base bets per 2-hour window (+ premium.bet + vote.bet). */
    maxBets: 1,
    shares: [0.01, 0.05, 0.1, 0.25, 0.33, 0.5],
    fixed: [25_000, 50_000, 100_000, 250_000],
    /** `generateCote`: 1.00–2.99, and 5.00–9.99 when the roll lands on exactly 1.00. */
    cote: { min: 100, max: 300, boostMin: 500, boostMax: 1000 },
    /** A lost bet is refunded on `getRandomInt(0, 100) === 0`. */
    refundRange: 100,
  },
  spot: {
    /** Lootcoin bids. */
    bids: [10, 100, 200, 300, 400, 500, 1_000],
    /** Monsters in the line-up (`LIST_MOB_LIST`); round r offers r + 1 of them, 7 rounds. */
    monsters: 8,
  },
} as const;
